package com.evoapps.evoshift.ui.workplaces

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.EditCalendar
import androidx.compose.material.icons.filled.Hub
import androidx.compose.material.icons.filled.Tune
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.SectionSource
import com.evoapps.evoshift.domain.model.SettingsSection
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.widgets.ProfileSection
import evoshift.composeapp.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/**
 * SH-269: how the settings cards show group settings. In a workplace: where each section's values
 * come from ("Group settings (name)" / "Own settings") with "Reset to group" or "Use own
 * settings"; in the group's own settings: how many of its workplaces use the group's values.
 * Provided once by the settings screen ([ProvideSectionSources]); a card calls [SectionSourceRow].
 */
class SectionSources(
    /** A workplace's section: where its values come from; null: no shared group (nothing shown). */
    val sourceOf: (SettingsSection) -> SectionSource?,
    /** The group's name as shown. */
    val groupName: String,
    /** Whether the person may switch [SettingsSection] (they may change its values). */
    val canSwitch: (SettingsSection) -> Boolean,
    val onSet: (SettingsSection, SectionSource) -> Unit,
    /** Group mode: (workplaces using the group's values, workplaces in the group); null in a workplace. */
    val groupCount: ((SettingsSection) -> Pair<Int, Int>?)? = null
)

val LocalSectionSources = staticCompositionLocalOf<SectionSources?> { null }

@Composable
fun ProvideSectionSources(sources: SectionSources?, content: @Composable () -> Unit) =
    CompositionLocalProvider(LocalSectionSources provides sources, content = content)

/** SH-269: test tag of a section's source line (by section id). */
fun sectionSourceTag(section: SettingsSection) = "section_source_${section.id}"

/** SH-269: test tag of a section's "Reset to group" / "Use own settings" button (by section id). */
fun sectionSourceButtonTag(section: SettingsSection) = "section_source_button_${section.id}"

/** The section's name (cards holding more than one section say which line is which). */
fun settingsSectionNameRes(section: SettingsSection): StringResource = when (section) {
    SettingsSection.INCOME -> Res.string.settings_section_income
    SettingsSection.SALES_TARGETS -> Res.string.settings_section_sales_targets
    SettingsSection.LABOUR_TARGETS -> Res.string.settings_section_labour_targets
    SettingsSection.REGION -> Res.string.settings_section_region
    SettingsSection.SWAP_POLICY -> Res.string.settings_section_swap_policy
    SettingsSection.ROLES_SKILLS -> Res.string.settings_section_roles_skills
    SettingsSection.AVAILABILITY_SLOTS -> Res.string.settings_section_availability_slots
    SettingsSection.TEMPLATES -> Res.string.settings_section_templates
    SettingsSection.SUNDAYS -> Res.string.settings_section_sundays
    SettingsSection.REQUESTS -> Res.string.settings_section_requests
    SettingsSection.REST_RULES -> Res.string.settings_section_rest_rules
    SettingsSection.STAFF_CHECKS -> Res.string.settings_section_staff_checks
    SettingsSection.PAYROLL -> Res.string.settings_section_payroll
    SettingsSection.BREAKS -> Res.string.settings_section_breaks
    SettingsSection.CLOCK_REVIEW -> Res.string.settings_section_clock_review
    SettingsSection.GEOFENCE -> Res.string.settings_section_geofence
    SettingsSection.ALERTS -> Res.string.settings_section_alerts
    SettingsSection.WORK_HOURS -> Res.string.settings_section_work_hours
    SettingsSection.REPORTS -> Res.string.settings_section_reports
    SettingsSection.ROSTER_VISIBILITY -> Res.string.settings_section_roster_visibility
    SettingsSection.WEATHER -> Res.string.settings_section_weather
    SettingsSection.SPECIAL_DAYS -> Res.string.settings_section_special_days
    SettingsSection.FOOTER -> Res.string.settings_section_footer
}

/**
 * Where [sections]' values come from, one line each (the section's name first when there are
 * several), inside a settings card. Nothing outside group settings.
 */
@Composable
fun SectionSourceRow(vararg sections: SettingsSection) {
    val sources = LocalSectionSources.current ?: return
    // The default group without a name of its own reads "All workplaces".
    val defaultName = stringResource(Res.string.workplace_groups_default_name)
    val groupName = sources.groupName.ifBlank { defaultName }
    sections.forEach { section ->
        val named = sections.size > 1
        sources.groupCount?.let { count ->
            val (following, total) = count(section) ?: return@forEach
            val text = stringResource(Res.string.group_settings_following_count, following, total)
            Text(
                if (named) stringResource(settingsSectionNameRes(section)) + ": " + text else text,
                style = MaterialTheme.typography.bodySmall,
                color = DeepNavy.copy(alpha = 0.7f),
                modifier = Modifier.testTag(sectionSourceTag(section))
            )
            return@forEach
        }
        val source = sources.sourceOf(section) ?: return@forEach
        val label = when (source) {
            SectionSource.GROUP -> stringResource(Res.string.group_settings_source_group, groupName)
            SectionSource.OWN -> stringResource(Res.string.group_settings_source_own)
        }
        // The line takes the card's width; the switch sits under it, at the end (phones are narrow).
        Column(Modifier.fillMaxWidth().testTag(sectionSourceTag(section))) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Icon(
                    if (source == SectionSource.GROUP) Icons.Default.Hub else Icons.Default.Tune,
                    contentDescription = null,
                    tint = if (source == SectionSource.GROUP) DarkGold else DeepNavy.copy(alpha = 0.6f),
                    modifier = Modifier.size(16.dp)
                )
                Text(
                    if (named) stringResource(settingsSectionNameRes(section)) + ": " + label else label,
                    style = MaterialTheme.typography.bodySmall,
                    fontWeight = FontWeight.Medium,
                    color = DeepNavy.copy(alpha = 0.8f),
                    modifier = Modifier.weight(1f)
                )
            }
            if (sources.canSwitch(section)) {
                TextButton(
                    onClick = { sources.onSet(section, if (source == SectionSource.GROUP) SectionSource.OWN else SectionSource.GROUP) },
                    modifier = Modifier.align(Alignment.End).testTag(sectionSourceButtonTag(section))
                ) {
                    Text(
                        stringResource(if (source == SectionSource.GROUP) Res.string.group_settings_make_own else Res.string.group_settings_reset),
                        style = MaterialTheme.typography.labelMedium
                    )
                }
            }
        }
    }
}

/** SH-269: test tag of the group settings' "Use the templates of …" button (by workplace id). */
fun groupTemplatesFromTag(workplaceId: String) = "group_templates_from_$workplaceId"

/**
 * SH-269 (the group's settings): what the group's settings are for, and the group's templates:
 * the owner picks the workplace whose shift and day templates and weekly skeleton the group takes.
 */
@Composable
fun GroupSettingsIntroCard() {
    ProfileSection(title = stringResource(Res.string.group_settings_title), icon = Icons.Default.Hub, onEdit = null) {
        Text(stringResource(Res.string.group_settings_intro), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.8f))
    }
}

@Composable
fun GroupTemplatesCard(workplaces: Map<String, String>, canEdit: Boolean, onUseTemplatesFrom: (String) -> Unit) {
    ProfileSection(title = stringResource(Res.string.group_settings_templates_title), icon = Icons.Default.EditCalendar, onEdit = null) {
        SectionSourceRow(SettingsSection.TEMPLATES)
        Text(stringResource(Res.string.group_settings_templates_hint), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f))
        if (canEdit) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(top = 4.dp)) {
                workplaces.forEach { (id, name) ->
                    OutlinedButton(onClick = { onUseTemplatesFrom(id) }, modifier = Modifier.fillMaxWidth().testTag(groupTemplatesFromTag(id))) {
                        Text(stringResource(Res.string.group_settings_templates_from, name))
                    }
                }
            }
        }
    }
}
